"use client";

import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { forwardRef, useEffect, useRef, useState } from "react";

export interface WaveCursorProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode;
	waveText: string;
	className?: string;
}

export const WaveCursor = forwardRef<HTMLDivElement, WaveCursorProps>(
	({ children, waveText, className, ...rest }, ref) => {
		const [isHover, setIsHover] = useState(false);
		const [shaking, setShaking] = useState(false);
		const [pos, setPos] = useState({ x: 0, y: 0 });
		const timeoutRef = useRef<number>(0);
		const cursorTimeoutRef = useRef<number>(0);

		useEffect(() => {
			if (isHover) {
				setShaking(true);
				timeoutRef.current = window.setTimeout(() => setShaking(false), 800);

				// Auto-hide cursor effect after 3 seconds on touch devices
				cursorTimeoutRef.current = window.setTimeout(
					() => setIsHover(false),
					3000,
				);
			} else {
				clearTimeout(timeoutRef.current);
				clearTimeout(cursorTimeoutRef.current);
				setShaking(false);
			}
			return () => {
				clearTimeout(timeoutRef.current);
				clearTimeout(cursorTimeoutRef.current);
			};
		}, [isHover]);

		const onMouseMove = (e: React.MouseEvent) => {
			setPos({ x: e.clientX, y: e.clientY });
		};
		const onMouseEnter = () => setIsHover(true);
		const onMouseLeave = () => setIsHover(false);

		// Handle touch events
		const onTouchStart = (e: React.TouchEvent) => {
			if (e.touches && e.touches.length > 0) {
				const touch = e.touches[0];
				if (touch) {
					setPos({ x: touch.clientX, y: touch.clientY });
					setIsHover(true);
				}
			}
		};

		const onTouchEnd = () => {
			// Set a short timeout to hide the cursor after touch ends
			window.setTimeout(() => setIsHover(false), 1000);
		};

		return (
			<div
				ref={ref}
				{...rest}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				onMouseMove={onMouseMove}
				onTouchStart={onTouchStart}
				onTouchEnd={onTouchEnd}
				onTouchCancel={onTouchEnd}
				className={cn("relative", className)}
			>
				{children}

				<AnimatePresence>
					{isHover && (
						<>
							<motion.div
								key="wave"
								className="pointer-events-none fixed z-[999] text-2xl"
								style={{ top: pos.y + 10, left: pos.x + 10 }}
								initial={false}
								animate={
									shaking ? { rotate: [0, 20, -20, 10, -10, 0] } : { rotate: 0 }
								}
								transition={{ duration: 0.8, ease: "easeInOut" }}
							>
								👋
							</motion.div>
							<motion.div
								key="tooltip"
								className="pointer-events-none fixed z-[999] select-none rounded bg-gray-800 px-2 py-1 text-white text-xs shadow-lg"
								style={{ top: pos.y + 10, left: pos.x + 36 }}
								initial={{ opacity: 0, y: -4 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -4 }}
								transition={{ duration: 0.15 }}
							>
								{waveText}
							</motion.div>
						</>
					)}
				</AnimatePresence>
			</div>
		);
	},
);

WaveCursor.displayName = "WaveCursor";
